import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SessionStorageService } from './session-storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CartItemRequest } from './cart-validation.service';

export interface CartItemStoreEntry {
  productId: number;
  formatKey: string;
  formatName: string;
  quantity: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {

  // Map key: `${productId}_${formatKey}` -> CartItemStoreEntry
  private items = new BehaviorSubject<Map<string, CartItemStoreEntry>>(new Map());
  private readonly cartKey = 'shoppingCartStateMultiFormat';

  public quantity$: Observable<number> = this.items.asObservable().pipe(
    map(itemsMap => {
      let totalQuantity = 0;
      itemsMap.forEach(item => totalQuantity += item.quantity);
      return totalQuantity;
    })
  );

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private sessionStorage: SessionStorageService
  ) {
    this.loadCart();
  }

  private updateCart(currentItems: Map<string, CartItemStoreEntry>): void {
    this.saveCart(currentItems);
    this.items.next(currentItems);
  }

  private saveCart(itemsToSave: Map<string, CartItemStoreEntry>): void {
    if (isPlatformBrowser(this.platformId)) {
      const cartArray = Array.from(itemsToSave.entries());
      this.sessionStorage.setItem(this.cartKey, cartArray);
    }
  }

  private loadCart(): void {
    if (isPlatformBrowser(this.platformId)) {
      const cartArray = this.sessionStorage.getItem(this.cartKey);
      if (cartArray && Array.isArray(cartArray)) {
        this.items.next(new Map<string, CartItemStoreEntry>(cartArray));
      }
    }
  }

  addItem(productId: number, formatKey: string = 'full', formatName: string = 'Botella Completa', quantity: number = 1): void {
    const currentItems = new Map(this.items.getValue());
    const key = `${productId}_${formatKey}`;
    const existing = currentItems.get(key);
    const newQty = existing ? existing.quantity + quantity : quantity;

    currentItems.set(key, { productId, formatKey, formatName, quantity: newQty });
    this.updateCart(currentItems);
  }

  updateItemQuantity(cartKey: string, quantity: number): void {
    const currentItems = new Map(this.items.getValue());
    const existing = currentItems.get(cartKey);
    if (existing) {
      if (quantity > 0) {
        existing.quantity = quantity;
        currentItems.set(cartKey, existing);
      } else {
        currentItems.delete(cartKey);
      }
      this.updateCart(currentItems);
    }
  }

  deleteItem(cartKey: string): void {
    const currentItems = new Map(this.items.getValue());
    if (currentItems.delete(cartKey)) {
      this.updateCart(currentItems);
    }
  }

  clearCart(): void {
    this.updateCart(new Map());
  }

  getCartState(): CartItemRequest[] {
    const itemsMap = this.items.getValue();
    return Array.from(itemsMap.values()).map(item => ({
      productId: item.productId,
      formatKey: item.formatKey,
      quantity: item.quantity
    }));
  }

  getCartItemsMap(): Map<string, CartItemStoreEntry> {
    return this.items.getValue();
  }
}
